import type { FairCapsuleItem } from '@/ui/tokens/fair-capsule.data'
import type { FairMegaMenuSize } from '@/ui/tokens/fair-mega-menu.data'
import type { MainPageCapsulesResponse } from '@/features/capsules/api/capsules'
import { mapMainPageOutfitToFairItems } from '@/features/capsules/api/capsules.data'

/** Home Capsules carousel card width — Figma `1:8735` (547px at 1920) */
export const FAIR_HOME_CAPSULES_CARD_WIDTH = 547

export const FAIR_HOME_CAPSULES_CARD_GAP = 40

export const FAIR_HOME_CAPSULES_AI_TIP_KEY = 'products_easy_to_combine' as const

export const FAIR_HOME_CAPSULES_DEFAULT_LIMIT = 10

export const FAIR_HOME_CAPSULES_DEFAULT_AUDIENCE = 'woman'

export interface FairHomeCapsuleSlide {
  id: number
  name: string
  description: string
  /** Looks inside this capsule (API `items`) */
  looks: FairCapsuleItem[][]
}

/** Map `GET /capsules` (§2.5) → Home carousel slides; capsules with no drawable look are skipped. */
export function mapCapsulesPreviewToFairSlides(
  response: MainPageCapsulesResponse | null | undefined,
): FairHomeCapsuleSlide[] {
  const capsules = response?.items ?? []
  if (!capsules.length) {
    return []
  }

  return capsules
    .map((capsule) => {
      const looks = (capsule.outfits ?? [])
        .map(mapMainPageOutfitToFairItems)
        .filter((items) => items.length > 0)

      return {
        id: capsule.id,
        name: capsule.name,
        description: capsule.description ?? '',
        looks,
      }
    })
    .filter((slide) => slide.looks.length > 0)
}

export function resolveFairHomeCapsuleSlides(
  response: MainPageCapsulesResponse | null | undefined,
): FairHomeCapsuleSlide[] {
  return mapCapsulesPreviewToFairSlides(response)
}

export function getFairHomeCapsuleLookItems(
  slide: FairHomeCapsuleSlide,
  lookIndex: number,
): FairCapsuleItem[] {
  if (!slide.looks.length) return []
  const safe =
    ((lookIndex % slide.looks.length) + slide.looks.length) %
    slide.looks.length
  return slide.looks[safe] ?? []
}

export function getFairHomeCapsulesMegaMenuSize(width: number): FairMegaMenuSize {
  if (width < 768) return '393'
  if (width < 1024) return '768'
  if (width < 1366) return '1366'
  // Keep 1440 tokens until true 1920 room — 80px gaps crush cards under ~1600.
  if (width < 1600) return '1440'
  return '1920'
}

export function getFairHomeCapsulesCardWidth(width: number): number {
  if (width < 768) return 268
  if (width < 1024) return 390
  return FAIR_HOME_CAPSULES_CARD_WIDTH
}

export function getFairHomeCapsulesPadInline(width: number): number {
  if (width < 768) return 16
  if (width < 1024) return 40
  if (width < 1440) return 60
  return 100
}

export function getFairHomeCapsulesCardsPerView(
  width: number,
  cardWidth: number,
  gap = FAIR_HOME_CAPSULES_CARD_GAP,
): number {
  if (width < 768) return 1
  if (width < 1024) return 2
  const available = width - getFairHomeCapsulesPadInline(width) * 2
  const fit = Math.floor((available + gap) / (cardWidth + gap))
  return Math.max(1, Math.min(fit, 3))
}

export function getFairHomeCapsulesCarouselPageCount(
  totalSlides: number,
  cardsPerView: number,
): number {
  if (totalSlides <= 0) return 1
  if (totalSlides <= cardsPerView) return 1
  return totalSlides - cardsPerView + 1
}

export function clampFairHomeCapsulesCarouselPage(
  page: number,
  totalPages: number,
): number {
  if (totalPages < 1) return 1
  const safe = Math.floor(page)
  if (safe < 1) return 1
  if (safe > totalPages) return totalPages
  return safe
}

export function getFairHomeCapsulesCarouselOffset(
  page: number,
  cardWidth: number,
  gap = FAIR_HOME_CAPSULES_CARD_GAP,
): number {
  return (page - 1) * (cardWidth + gap)
}
